import React, { Component } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getTextureUrl } from '../../../Common/TextureTools';

import './action-scroller.css';
import { ASSETS_URL } from '../../../../../Settings';
import { DEFAULT_ACTION_HEX_POSITION } from '../../../../Game/GamePlayers/GamePlayerController';

// 2026-05-16: ActionChooser rewrite. The 12 rendered SwiperSlides
// hold textures in this order, two per action:
//   index 0,1   → Research (texture 71)
//   index 2,3   → Lodging  (texture 72)
//   index 4,5   → Staff    (texture 73)
//   index 6,7   → Renovation (texture 74)
//   index 8,9   → Advertisement (texture 75)
//   index 10,11 → Ghost (texture 76)
// With `slidesPerView=6` and `centeredSlides=false`, the leftmost
// visible slide is `swiper.realIndex`. The two slides at the
// visual *center* of the visible 6 are `realIndex+2` and
// `realIndex+3` (mod 12). That pair is what we want the backend
// to treat as the player's choice, mapped into
// INVERTED_WHEEL_CHOICES.
//
// The old code used `swiper.activeIndex % 12` which, in
// `loop={true}` mode, includes Swiper's duplicate leading slides
// and is shifted by `loopedSlides` (typically 6). It also used a
// (-3, -2) offset and a separately mis-shifted LOOP array; the
// two errors partially canceled for some positions which is why
// the slider felt "inconsistent — sometimes right." Replaced
// with `swiper.realIndex` plus the math below.

// Action id (1..6) shown on slide N (0..11). Two slides per action.
const slideAction = (slideIndex) =>
    Math.floor(((slideIndex % 12) + 12) % 12 / 2) + 1;

// realIndex K (leftmost visible) → INVERTED_WHEEL_CHOICES position.
// Worked out from the visible-center pair: K=10 shows Research at
// visual center → pos 0 (INVERTED[0]=[1,1]); K=0 shows Lodging at
// center → pos 10 (INVERTED[10]=[2,2]); K=8 shows Ghost focus → pos 2.
const realIndexToPosition = (K) => (10 - K + 12) % 12;
const positionToRealIndex = (P) => (10 - P + 12) % 12;


export default class ActionChooser extends Component {
    constructor(props) {
        super(props);
        this.api = this.props.app.game.seatedController.gamePlayerApi;
        this.seat = this.props.gamePlayerData.seat;
        this.currentActions = [ null, null ];
        this.currentPosition = null;
        this.currentSwiperIndex = null;
        this.firstLoad = true;
        // Tracks action_hex_locked across per-seat events so we can detect
        // the lock→unlock transition that marks a new turn beginning on
        // the local seat. Seeded from the initial gamePlayerData prop so
        // the first event after mount doesn't read undefined→falsy and
        // misfire on first-turn render. See gamePlayerUpdateListener.
        this._wasLocked = !!(this.props.gamePlayerData
            && this.props.gamePlayerData.action_hex_locked);
        // Set by Swiper user-gesture callbacks (onTouchStart, onClick).
        // onSlideChange checks this flag before emitting — layout
        // reflows that fire onSlideChange without prior user input
        // never reach the emitter. See 2026-05-19 loop fix.
        this.userInputPending = false;
        // game-player-N-event is the per-seat broadcast emitted by
        // GamePlayerController.updateGamePlayerData() whenever new
        // server state lands. We can't rely on React's normal
        // re-render path here because the Swiper instance is wrapped
        // in shouldComponentUpdate=false. So listen on the event
        // directly and call slideToLoop when action_hex_position
        // changes from underneath us (lock-state transition, opponent
        // updates, etc.). For OWN seat we ignore action_hex_position
        // changes — local intent is authoritative. 2026-05-19.
        this.gamePlayerEventName =
            `game-player-${this.seat}-event`;
    }

    componentDidMount() {
        window.addEventListener('action-hex-event',
            this.actionHexListener);
        window.addEventListener(this.gamePlayerEventName,
            this.gamePlayerUpdateListener);
    }

    componentWillUnmount() {
        window.removeEventListener('action-hex-event',
            this.actionHexListener);
        window.removeEventListener(this.gamePlayerEventName,
            this.gamePlayerUpdateListener);
    }

    isLocalSeat = () => {
        const seated = this.props.app && this.props.app.game
            && this.props.app.game.seatedController;
        return !!(seated && seated.seat === this.seat);
    }

    isHexLocked = () => {
        return !!(this.props.gamePlayerData
            && this.props.gamePlayerData.action_hex_locked);
    }

    gamePlayerUpdateListener = (e) => {
        const gp = e.detail;
        if (!gp) return;

        if (this.isLocalSeat()) {
            // Lock→unlock transition = new turn beginning on this seat.
            // Swiper loop-mode silently recalculates realIndex on
            // container reflow; the userInputPending guard suppresses
            // the broadcast of those reflow-driven onSlideChange fires
            // (so state stays correct) but does NOT pull the visual
            // back to the authoritative position. By the time the next
            // turn starts, the Swiper's internal realIndex can be
            // anywhere — and nothing else re-syncs it for the local
            // seat because of the early-return below (which is itself
            // correct: it's the cut that breaks the auto-rotate loop
            // on engine echoes). Snap the visual back here, exactly
            // once per turn boundary. 2026-05-22.
            const wasLocked = this._wasLocked === true;
            const isLocked = gp.action_hex_locked === true;
            this._wasLocked = isLocked;
            if (wasLocked && !isLocked
                    && this.swiper && !this.firstLoad) {
                const pos = gp.action_hex_position;
                if (pos !== null && pos !== undefined) {
                    const target = positionToRealIndex(pos);
                    if (this.currentSwiperIndex !== target) {
                        this.currentPosition = pos;
                        this.currentSwiperIndex = target;
                        this.currentActions = [
                            slideAction(target + 2),
                            slideAction(target + 3),
                        ];
                        // runCallbacks=false so the snap-settle's
                        // onSlideChange does not fire the emit path
                        // and re-broadcast our own state back through
                        // the seated controller. Same discipline as
                        // the opponent-seat path below.
                        this.swiper.slideToLoop(target, 0, false);
                    }
                }
            }
            return;
        }

        const incomingPosition = gp.action_hex_position;
        if (incomingPosition === null || incomingPosition === undefined) {
            return;
        }
        if (this.currentPosition === incomingPosition) return;
        if (!this.swiper) return;
        if (this.firstLoad) return;
        // slideToLoop with runCallbacks=false suppresses onSlideChange
        // so we don't echo this backend-supplied position back to the
        // backend as a fresh Click. Used only for OPPONENT hexes.
        const newRealIndex = positionToRealIndex(incomingPosition);
        this.currentPosition = incomingPosition;
        this.currentSwiperIndex = newRealIndex;
        this.currentActions = [
            slideAction(newRealIndex + 2),
            slideAction(newRealIndex + 3),
        ];
        this.swiper.slideToLoop(newRealIndex, 0, false);
    }

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        return false;
    }

    actionImage = (props) => {
        return <img
            src={getTextureUrl(props.texture)}
            alt="action"
        />;
    };

    getAndSendActions = (realIndex) => {
        const center1Id = slideAction(realIndex + 2);
        const center2Id = slideAction(realIndex + 3);
        this.currentActions = [center1Id, center2Id];
        const position = realIndexToPosition(realIndex);
        this.currentPosition = position;
        window.dispatchEvent(new CustomEvent('action-hex-event', {
            detail: { seat: this.seat, actionHexPosition: position }
        }));
    };

    onTouchStart = () => {
        if (this.isHexLocked()) return;
        this.userInputPending = true;
    }

    onTouchMove = () => {
        // Re-arm on every move during the drag. Without this,
        // userInputPending is consumed after the first slide-change
        // tick and every subsequent clockwork tick in the same drag
        // gets dropped by the guard in onSlideChange. The slider
        // visually snaps to the final position but the hex/grid lock
        // to whatever position the first tick reached. 2026-05-22.
        if (this.isHexLocked()) return;
        this.userInputPending = true;
    }

    onTouchEnd = (swiper) => {
        // Release-driven commit: whatever realIndex the slider is
        // settling on at finger-up, emit it. Catches the case where
        // the drag crossed boundaries faster than onSlideChange fired
        // for each, and the case where the drag didn't cross a
        // boundary at all but the user expected the position to
        // commit. The snap-settle animation that follows release
        // will fire onSlideChange one more time; userInputPending
        // is left true so that fire also passes the reflow guard
        // and the final settled realIndex wins. 2026-05-22.
        if (this.isHexLocked()) return;
        if (this.firstLoad) return;
        this.userInputPending = true;
        if (this.currentSwiperIndex !== swiper.realIndex) {
            this.currentSwiperIndex = swiper.realIndex;
            this.getAndSendActions(swiper.realIndex);
        }
    }

    handleSwiper = (swiper) => {
        // 2026-05-23: Seed the initial slider position HERE (inside
        // onSwiper) rather than deferring to the firstLoad branch of
        // onSlideChange. Swiper with `loop=true` fires an internal
        // layout-driven `onSlideChange` during loop-slide duplication
        // setup, which can land BEFORE `onSwiper` resolves. The old
        // pattern read this.swiper inside that firstLoad branch and
        // skipped slideToLoop when this.swiper was undefined, but
        // flipped firstLoad to false unconditionally — locking the
        // seed out forever. In dev, React 19 StrictMode's mount-
        // unmount-mount cycle masked the race (the second mount
        // happens after the Swiper container settles, so onSwiper
        // wins). In prod, no StrictMode, race lands, slider sits at
        // realIndex 0 while currentSwiperIndex says 10 → consistent
        // -3 / +9 (mod 12) visual offset against the action grid.
        // Seeding from onSwiper guarantees swiper is the just-
        // resolved instance, eliminating the race entirely.
        this.swiper = swiper;
        if (!this.firstLoad) return;
        const raw = this.props.gamePlayerData.action_hex_position;
        const position = (raw === null || raw === undefined)
            ? DEFAULT_ACTION_HEX_POSITION : raw;
        const initialSlide = positionToRealIndex(position);
        this.currentPosition = position;
        this.currentSwiperIndex = initialSlide;
        this.firstLoad = false;
        if (swiper && swiper.slideToLoop) {
            swiper.slideToLoop(initialSlide, 0);
        }
    }

    onSlideChange = (swiper) => {
        // Fires during drag as each clockwork tick crosses a slide
        // boundary, and during the snap-settle after release. Use
        // realIndex so loop-mode duplicate slides don't shift us.
        this.swiper = swiper;
        if (this.firstLoad) {
            // Loop-mode internal layout sweep fired before onSwiper.
            // Don't process — handleSwiper will seed when it lands.
            return;
        }
        if (this.isHexLocked()) {
            this.userInputPending = false;
            return;
        }
        // Layout reflows (resize, container measure) fire onSlideChange
        // without any preceding user gesture. The userInputPending
        // flag (armed by onTouchStart, re-armed by onTouchMove) is
        // true only while a finger is actively interacting; drop
        // reflow fires that don't carry user input. 2026-05-19/05-22.
        if (!this.userInputPending) return;
        this.updateSwiperIndex(swiper.realIndex);
    };

    updateSwiperIndex = (realIndex) => {
        // 2026-05-23: firstLoad seeding moved out of here and into
        // handleSwiper (the onSwiper callback). See handleSwiper's
        // comment for the race-condition fix. We still guard against
        // firing during the pre-seed window — if onSlideChange races
        // ahead of onSwiper, just return; the seed will run when
        // handleSwiper lands.
        if (this.firstLoad) return;
        if (this.currentSwiperIndex !== realIndex) {
            this.currentSwiperIndex = realIndex;
            this.getAndSendActions(realIndex);
        }
    }

    actionHexListener = (e) => {
        if (this.seat === e.detail.seat) {
            const position = e.detail.actionHexPosition;
            if (this.currentPosition !== position) {
                this.setSlider(position);
            }
        }
    }

    setSlider = (position) => {
        // slideToLoop takes the real (un-shifted) index, which is
        // what we want regardless of which duplicate cycle Swiper
        // currently has the user on.
        this.swiper.slideToLoop(positionToRealIndex(position), 0);
    }

    // 2026-05-22: onClick handler removed. Tap-to-snap-clicked-slide
    // was interfering with drag — Swiper's tap detection fires
    // before drag detection settles on quick tap-then-drag motions,
    // so the slider would snap to the touchdown position before the
    // drag could move it. Slider is drag-only now. Taps on slides
    // do nothing; commits happen via the action grid below
    // (ActionSelector).

    render() {
        return (
            <div className={'action-scroller'}
                 data-ftue-target={'action-hex'}>
                <div className={'action-scroller-anchor'}>
                    <div className={'swiper-container'}>
                        <Swiper
                            slidesPerView={6}
                            spaceBetween={0}
                            // freeMode={true}
                            grabCursor={true}
                            loop={true}
                            watchSlidesProgress={true}
                            onSwiper={this.handleSwiper}
                            onTouchStart={this.onTouchStart}
                            onTouchMove={this.onTouchMove}
                            onTouchEnd={this.onTouchEnd}
                            onSlideChange={this.onSlideChange}
                            // initialSlide={initialSlide}
                            // centeredSlides={false}
                            className="action-scroller-swiper"
                        >
                            <SwiperSlide virtualIndex={1}>
                                <this.actionImage texture={71}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={2}>
                                <this.actionImage texture={71}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={3}>
                                <this.actionImage texture={72}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={4}>
                                <this.actionImage texture={72}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={5}>
                                <this.actionImage texture={73}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={6}>
                                <this.actionImage texture={73}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={7}>
                                <this.actionImage texture={74}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={8}>
                                <this.actionImage texture={74}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={9}>
                                <this.actionImage texture={75}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={10}>
                                <this.actionImage texture={75}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={11}>
                                <this.actionImage texture={76}/>
                            </SwiperSlide>
                            <SwiperSlide virtualIndex={12}>
                                <this.actionImage texture={76}/>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <img
                        src={`${ASSETS_URL}overlays/ActionSliderLayover-1080x210.png`}
                        alt="action"
                        className={'swiper-overlay'}
                    />
                </div>

            </div>
        )
    }
}
